import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { GlobalHotKeys } from "react-hotkeys";

export const useModal = () => {
  const [isOpen, setIsOpen] = useState(false);

  const open = () => {
    setIsOpen(true);
  };

  const close = () => {
    setIsOpen(false);
  };

  return { open, close, isOpen };
};

export const ModalPortal = ({ isOpen, children }) => {
  const modalRoot = document.body;
  const modalElementRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      if (!modalElementRef.current) {
        const el = document.createElement("dialog");
        el.className = "modal show text-body";
        el.tabIndex = -1;
        el.style.display = "block";
        el.open = true;

        modalRoot.appendChild(el);
        document.body.classList.add("modal-open");
        modalElementRef.current = el;
      } else {
        // Make sure the modal is not closed if one already exists
        const el = modalElementRef.current;
        el.open = true;
        el.style.display = "block";
      }
    }
    return () => {
      if (modalElementRef.current) {
        document.body.classList.remove("modal-open");
        modalRoot.removeChild(modalElementRef.current);
        modalElementRef.current = null;
      }
    };
  }, [isOpen, modalRoot]);

  if (!isOpen || !modalElementRef.current) return null;

  return createPortal(children, modalElementRef.current);
};

export const Modal = ({
  title,
  children,
  isOpen,
  onClose,
  isScrollable,
  size,
  bodyClassName = "",
}) => {
  const scrollableClassName = isScrollable && "modal-dialog-scrollable";
  const sizeClassName = size && `modal-${size}`;

  const MODAL_KEY_MAP = {
    ESCAPE: ["esc"],
  };

  const modalKeymapHandler = {
    ESCAPE: onClose,
  };

  return (
    isOpen && (
      <ModalPortal isOpen={isOpen} size={size}>
        <GlobalHotKeys keyMap={MODAL_KEY_MAP} handlers={modalKeymapHandler} />
        <div className={`modal-dialog ${scrollableClassName} ${sizeClassName}`}>
          <div className="modal-content">
            <div className="modal-header bg-primary text-white">
              <h5 className="modal-title">{title}</h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                aria-label="Close"
                onClick={onClose}
              >
              </button>
            </div>
            <div className={`modal-body ${bodyClassName}`}>{children}</div>
          </div>
        </div>
      </ModalPortal>
    )
  );
};
